import HealthcareCase from '../components/HealthcareCase';
import SlidingChoice from '../components/SlidingChoice';
import '../components/CompactSwitch.css';
import CounterpartyFields from '../components/CounterpartyFields';
import React, { useState, useEffect } from 'react';
import { FaInfoCircle, FaMapMarkerAlt } from 'react-icons/fa';
import ComuneAutocomplete from '../components/ComuneAutocomplete';
import PracticeDocumentLinks from '../components/PracticeDocumentLinks';
import CompanyContactModal, { emptyCompanyContact } from '../components/CompanyContactModal';
import "../pages/DatiSinistro.css";

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  return dateString.split('T')[0]; 
};
const prescriptionFromDate = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return '';
  const [year, month, day] = value.split('-').map(Number);
  const maxDay = new Date(Date.UTC(year + 2, month, 0)).getUTCDate();
  return `${year + 2}-${String(month).padStart(2, '0')}-${String(Math.min(day, maxDay)).padStart(2, '0')}`;
};

const googleMapsUrl = luogo => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((luogo || '').trim())}`;

const CompanyAutocomplete = ({ value, companies, onSelect, onCreate, ariaLabel }) => {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  useEffect(() => { if (!open) setQuery(value || ''); }, [value, open]);
  const normalized = query.trim().toLocaleLowerCase('it');
  const matches = companies.filter(company => !normalized || company.nome.toLocaleLowerCase('it').includes(normalized)).slice(0, 7);
  const exact = companies.some(company => company.nome.toLocaleLowerCase('it') === normalized);
  return <div className="company-autocomplete">
    <input type="text" aria-label={ariaLabel} placeholder="Scrivi il nome della compagnia"
      value={query} autoComplete="off" onFocus={() => setOpen(true)}
      onChange={event => { setQuery(event.target.value); setOpen(true); }}
      onBlur={() => window.setTimeout(() => setOpen(false), 160)} />
    {open && <div className="company-suggestions">
      {matches.map(company => <button type="button" key={company.id} onMouseDown={event => event.preventDefault()}
        onClick={() => { onSelect(company); setQuery(company.nome); setOpen(false); }}><strong>{company.nome}</strong><small>Seleziona</small></button>)}
      {normalized && !exact && <button type="button" className="create" onMouseDown={event => event.preventDefault()}
        onClick={() => { onCreate(query.trim()); setOpen(false); }}><strong>+ Aggiungi “{query.trim()}”</strong><small>Nuova compagnia in Rubrica</small></button>}
      {!matches.length && !normalized && <p>Inizia a scrivere per cercare.</p>}
    </div>}
  </div>;
};

const DatiSinistro = ({ data, setData, onCompanyChange, clienteNome = '', tipoPratica = '', schedaId, suspendedId, showDocumentLinks = true }) => {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const suggestedDemandDate = data.dataApertura || today;
  const setDemandDate = value => setData(previous => ({ ...previous, dataMessaInMora: value,
    scadenzaPrescrizione: tipoPratica==='RC professionale'?previous.scadenzaPrescrizione:prescriptionFromDate(previous.ultimaInterruzione && previous.ultimaInterruzione > value ? previous.ultimaInterruzione : value || previous.dataSinistro) }));
  const vehicleTerms = ['Auto', 'RCA'].includes(tipoPratica) && data.mezzi?.[0]?.tipoMezzo !== 'cose';
  const caiTipo = data.caiTipo || (data.caiDueFirme ? 'doppia' : 'assente');
  const isHealthcare = tipoPratica === 'RC professionale';
  const isOtherLiability = tipoPratica === 'RC diversi' || isHealthcare;
  const isAccidentPolicy = tipoPratica === 'Polizza infortuni';
  const isPedestrian = tipoPratica === 'Pedone';
  const autoritaPresenti = data.autoritaPresenti ?? Boolean(data.autoritaIntervenuta || data.cittaAutorita);
  const [compagnieAssicurative, setCompagnieAssicurative] = useState([]);
  const [showBanner, setShowBanner] = useState(false);
  const [newCompagnia, setNewCompagnia] = useState(emptyCompanyContact());
  const [newCompagniaIndex, setNewCompagniaIndex] = useState(null);
  const clientVehicleSuggestion = React.useRef('');
  const [companyError, setCompanyError] = useState('');
  const currentCompanyName = (companyId, fallback = '') =>
    compagnieAssicurative.find(item => Number(item.id) === Number(companyId))?.nome || fallback;
  const vehicleLabel = index => isPedestrian
    ? (index === 0 ? 'Veicolo di controparte' : `Veicolo di controparte ${index + 1}`)
    : (index === 0 ? (data.mezzi?.[0]?.tipoMezzo === 'cose' ? 'Bene danneggiato del cliente' : 'Veicolo cliente') : index === 1 ? 'Veicolo controparte' : `Veicolo ${index + 1}`);

  useEffect(() => {
    const suggestedName = clienteNome.trim();
    if (schedaId || tipoPratica !== 'RCA' || !suggestedName || !data.mezzi?.length || clientVehicleSuggestion.current === suggestedName) return;
    clientVehicleSuggestion.current = suggestedName;
    setData(previous => {
      const firstVehicle = previous.mezzi?.[0];
      if (!firstVehicle || (firstVehicle.proprietario && firstVehicle.conducente)) return previous;
      return { ...previous, mezzi: previous.mezzi.map((mezzo, index) => index === 0 ? {
        ...mezzo,
        proprietario: mezzo.proprietario || suggestedName,
        conducente: mezzo.conducente || suggestedName
      } : mezzo) };
    });
  }, [schedaId, clienteNome, tipoPratica, data.mezzi?.length, setData]);

  useEffect(() => {
    if (schedaId || !isPedestrian || !data.mezzi?.length || data.mezzi.some(item => item.isRisarcitoria)) return;
    setData(previous => ({ ...previous,
      compagniaId: previous.mezzi[0]?.compagniaId || previous.compagniaId || null,
      nomeCompagnia: previous.mezzi[0]?.compagnia || previous.nomeCompagnia || '',
      mezzi: previous.mezzi.map((item, index) => ({ ...item, isRisarcitoria: index === 0 }))
    }));
  }, [schedaId, isPedestrian, data.mezzi, setData]);

  useEffect(() => {
    const fetchCompagnieAssicurative = async () => {
      try {
        const response = await fetch('/api/compagnie-assicurative');
        if (!response.ok) throw new Error('Errore durante il recupero delle compagnie');
        const compagnie = await response.json();
        setCompagnieAssicurative(compagnie);
      } catch (error) {
        console.error('Errore:', error);
      }
    };
    fetchCompagnieAssicurative();
  }, []);

  const handleChange = (field, value, index = null) => {
    if (index !== null) {
      const updatedMezzi = data.mezzi.map((mezzo, i) =>
        i === index ? {
          ...mezzo,
          [field]: value,
          ...(field === 'proprietario' && mezzo.conducenteComeProprietario ? { conducente: value } : {})
        } : mezzo
      );
      setData((prev) => ({ ...prev, mezzi: updatedMezzi }));
    } else {
      setData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleCompagniaChange = (value, index) => {
    if (value === '__new__') { setNewCompagniaIndex(index); setNewCompagnia(emptyCompanyContact()); setCompanyError(''); setShowBanner(true); return; }
    const company = compagnieAssicurative.find(item => String(item.id) === value);
    if (data.mezzi[index]?.isRisarcitoria && Number(data.compagniaId) !== Number(company?.id || 0)) onCompanyChange?.();
    setData(previous => ({ ...previous,
      compagniaId: previous.mezzi[index]?.isRisarcitoria ? (company?.id || null) : previous.compagniaId,
      nomeCompagnia: previous.mezzi[index]?.isRisarcitoria ? (company?.nome || '') : previous.nomeCompagnia,
      datiCompagnia: previous.mezzi[index]?.isRisarcitoria && Number(previous.compagniaId) !== Number(company?.id || 0)
        ? { ...previous.datiCompagnia, ispettoratoId: null, liquidatoreId: null } : previous.datiCompagnia,
      mezzi: previous.mezzi.map((mezzo, i) => i === index
        ? { ...mezzo, compagnia: company?.nome || '', compagniaId: company?.id || null,
            numeroSinistro: Number(mezzo.compagniaId || 0) === Number(company?.id || 0) ? (mezzo.numeroSinistro || '') : '' } : mezzo)
    }));
  };
  const setAutoritaPresenti = checked => setData(previous => ({ ...previous,
    autoritaPresenti: checked,
    ...(checked ? {} : { autoritaIntervenuta: '', cittaAutorita: '' })
  }));

  const impostaConducenteComeProprietario = (index, checked) => {
    setData(previous => ({
      ...previous,
      mezzi: previous.mezzi.map((mezzo, i) => i === index
        ? { ...mezzo, conducenteComeProprietario: checked, conducente: checked ? (mezzo.proprietario || '') : mezzo.conducente }
        : mezzo)
    }));
  };

  const setRisarcitoria = index => {
    if (Number(data.compagniaId) !== Number(data.mezzi[index]?.compagniaId || 0)) onCompanyChange?.();
    setData(previous => ({ ...previous,
    compagniaId: previous.mezzi[index]?.compagniaId || null,
    nomeCompagnia: currentCompanyName(previous.mezzi[index]?.compagniaId, previous.mezzi[index]?.compagnia || ''),
    datiCompagnia: Number(previous.compagniaId) !== Number(previous.mezzi[index]?.compagniaId || 0)
      ? { ...previous.datiCompagnia, ispettoratoId: null, liquidatoreId: null } : previous.datiCompagnia,
    mezzi: previous.mezzi.map((mezzo, i) => ({ ...mezzo, isRisarcitoria: i === index }))
    }));
  };

  const aggiungiMezzo = () => {
    setData((prev) => ({
      ...prev,
      mezzi: [...prev.mezzi, { tipoMezzo: 'auto', proprietario: !prev.mezzi.length && tipoPratica === 'RCA' ? clienteNome : '', veicolo: '', targa: '', conducente: !prev.mezzi.length && tipoPratica === 'RCA' ? clienteNome : '', conducenteComeProprietario: false, compagnia: '', numeroSinistro: '', isRisarcitoria: isPedestrian && !prev.mezzi.length }],
    }));
  };

  const rimuoviMezzo = (index) => {
    const mezzo = data.mezzi[index] || {};
    const label = vehicleLabel(index);
    const detail = [mezzo.veicolo, mezzo.targa].filter(Boolean).join(' · ');
    if (!window.confirm(`Rimuovere ${label}${detail ? ` (${detail})` : ''}?\n\nI dati inseriti per questo veicolo verranno eliminati dalla pratica.`)) return;
    setData((prev) => ({
      ...prev,
      mezzi: prev.mezzi.filter((_, i) => i !== index),
    }));
  };

  const handleSaveNewCompagnia = async company => {
    try {
      if (!company.nome.trim()) { setCompanyError('Inserisci la ragione sociale.'); return; }
      const response = await fetch('/api/contatti', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...company, tipo: 'Assicurazione', tipo_soggetto: 'azienda',
          nome: company.nome.trim(), partita_iva: String(company.partita_iva || '').toUpperCase() }),
      });
      if (!response.ok) { const detail = await response.json().catch(() => ({})); throw new Error(detail.error || 'Errore durante il salvataggio'); }
      const contact = await response.json();
      const detail = await fetch(`/api/contatti/${contact.id}`).then(result => result.json());
      const nuovaCompagnia = { id: detail.compagnia_id, nome: detail.nome };
      if (newCompagniaIndex === -1) {
        onCompanyChange?.();
        setCompagnieAssicurative((prev) => [...prev, nuovaCompagnia].sort((a, b) => a.nome.localeCompare(b.nome)));
        setData(previous => ({ ...previous, compagniaId: nuovaCompagnia.id, nomeCompagnia: nuovaCompagnia.nome }));
        setShowBanner(false); setCompanyError(''); return;
      }
      if (data.mezzi[newCompagniaIndex]?.isRisarcitoria) onCompanyChange?.();
      setCompagnieAssicurative((prev) => [...prev, nuovaCompagnia].sort((a, b) => a.nome.localeCompare(b.nome)));
      setData(previous => ({ ...previous,
        compagniaId: previous.mezzi[newCompagniaIndex]?.isRisarcitoria ? nuovaCompagnia.id : previous.compagniaId,
        nomeCompagnia: previous.mezzi[newCompagniaIndex]?.isRisarcitoria ? nuovaCompagnia.nome : previous.nomeCompagnia,
        mezzi: previous.mezzi.map((mezzo, i) => i === newCompagniaIndex
          ? { ...mezzo, compagnia: nuovaCompagnia.nome, compagniaId: nuovaCompagnia.id } : mezzo)
      }));
      setShowBanner(false);
      setCompanyError('');
    } catch (error) {
      setCompanyError(error.message);
    }
  };
  const handlePolicyCompanyChange = value => {
    if (value === '__new__') { setNewCompagniaIndex(-1); setNewCompagnia(emptyCompanyContact()); setShowBanner(true); return; }
    const company = compagnieAssicurative.find(item => Number(item.id) === Number(value));
    setData(previous => ({ ...previous, compagniaId: value ? Number(value) : null, nomeCompagnia: company?.nome || '' }));
    onCompanyChange?.();
  };
  const openNewCompany = (index, suggestedName = '') => {
    setNewCompagniaIndex(index); setNewCompagnia(emptyCompanyContact(suggestedName)); setCompanyError(''); setShowBanner(true);
  };

  if(isHealthcare) return <div className="dati-sinistro-container healthcare-compact-grid">
    <section className="box"><h2>Strutture e assicurazioni</h2><div className="sinistro-form"><CounterpartyFields healthcare value={data.controparte||{}} onChange={v=>handleChange('controparte',v)}/><div className="input-container"><label>Compagnia assicuratrice</label><CompanyAutocomplete ariaLabel="Compagnia della struttura" value={currentCompanyName(data.compagniaId,data.nomeCompagnia)} companies={compagnieAssicurative} onSelect={c=>setData(p=>({...p,compagniaId:c.id,nomeCompagnia:c.nome}))} onCreate={name=>openNewCompany(-1,name)}/></div><label className="input-container">Numero di polizza<input value={data.numeroPolizza||''} onChange={e=>handleChange('numeroPolizza',e.target.value)}/></label><label className="input-container">Numero sinistro della compagnia<input value={data.numeroSinistroCompagnia||''} onChange={e=>handleChange('numeroSinistroCompagnia',e.target.value)}/></label></div>
    {(data.sanitario?.controparti||[]).map((person,i)=><details key={i} className="healthcare-counterparty" open><summary>{person.nome||'Altra struttura / professionista'}</summary><div className="sinistro-form"><CounterpartyFields healthcare value={person} onChange={v=>handleChange('sanitario',{...data.sanitario,controparti:data.sanitario.controparti.map((x,j)=>i===j?v:x)})}/><div className="input-container"><label>Compagnia assicuratrice</label><CompanyAutocomplete ariaLabel="Compagnia altra struttura" value={currentCompanyName(person.compagniaId,person.compagnia)} companies={compagnieAssicurative} onSelect={c=>handleChange('sanitario',{...data.sanitario,controparti:data.sanitario.controparti.map((x,j)=>i===j?{...x,compagniaId:c.id,compagnia:c.nome}:x)})} onCreate={name=>openNewCompany(-1,name)}/></div><label className="input-container">Numero di polizza<input value={person.numeroPolizza||''} onChange={e=>handleChange('sanitario',{...data.sanitario,controparti:data.sanitario.controparti.map((x,j)=>i===j?{...x,numeroPolizza:e.target.value}:x)})}/></label></div><button type="button" onClick={()=>handleChange('sanitario',{...data.sanitario,controparti:data.sanitario.controparti.filter((_,j)=>j!==i)})}>Rimuovi collegamento</button></details>)}
    <button type="button" className="add-button" onClick={()=>handleChange('sanitario',{...data.sanitario,controparti:[...(data.sanitario?.controparti||[]),{}]})}>＋ Altra struttura / professionista</button></section>
    <section className="box healthcare-case"><h2>Caso sanitario</h2><div className="sinistro-form healthcare-dates"><label className="input-container">Data dell’evento<input type="date" value={formatDateForInput(data.dataSinistro)} onChange={e=>handleChange('dataSinistro',e.target.value)}/></label><label className="input-container">Data di messa in mora<input type="date" value={formatDateForInput(data.dataMessaInMora)} onChange={e=>handleChange('dataMessaInMora',e.target.value)}/></label><label className="input-container">Prescrizione<input type="date" value={formatDateForInput(data.scadenzaPrescrizione)} onChange={e=>handleChange('scadenzaPrescrizione',e.target.value)}/></label><label className="input-container healthcare-wide">Cronistoria del cliente<textarea data-autoformat="sentence" rows="5" maxLength="15000" value={data.sanitario?.cronistoria||''} onChange={e=>handleChange('sanitario',{...data.sanitario,cronistoria:e.target.value})}/></label></div><PracticeDocumentLinks schedaId={schedaId} suspendedId={suspendedId} documentKeys={['clientHistory']}/></section>
    {showBanner&&<CompanyContactModal value={newCompagnia} onChange={setNewCompagnia} onClose={()=>setShowBanner(false)} onSubmit={handleSaveNewCompagnia} error={companyError} submitLabel="Salva e seleziona"/>}
  </div>;
  return (
    <div className="dati-sinistro-container">
      <div className="sinistro-details-grid">
        <div className="box">
          <h2>Evento</h2>
          <div className="sinistro-form">
            <div className="input-container accident-location-field">
              <label htmlFor="luogo-sinistro">Luogo del sinistro</label>
              <div className="accident-location-row">
                <input id="luogo-sinistro" type="text"
                  placeholder="Via, numero civico, città o punto di riferimento"
                  value={data.luogoSinistro || ''}
                  onChange={event => handleChange('luogoSinistro', event.target.value)} required />
                <a className={`maps-link ${data.luogoSinistro?.trim() ? '' : 'is-disabled'}`}
                  href={data.luogoSinistro?.trim() ? googleMapsUrl(data.luogoSinistro) : undefined}
                  target="_blank" rel="noreferrer" aria-disabled={!data.luogoSinistro?.trim()}
                  onClick={event => { if (!data.luogoSinistro?.trim()) event.preventDefault(); }}
                  title="Apri il luogo su Google Maps">
                  <FaMapMarkerAlt aria-hidden="true" /><span>Maps</span>
                </a>
              </div>
              <small>Inserisci il luogo nel modo più preciso possibile.</small>
            </div>
            <div className="accident-date-time-row">
            <div className="input-container">
              <div className="input-label">
                <label htmlFor="accident-date">Data del sinistro</label>
                <span className="tooltip" title="Data in cui è avvenuto l'incidente"><FaInfoCircle className="icon-info" /></span>
                <span className="required-asterisk">*</span>
              </div>
              <input id="accident-date" type="date" value={formatDateForInput(data.dataSinistro)}
                onChange={(e) => setData({...data, dataSinistro: e.target.value, scadenzaPrescrizione: isHealthcare || data.dataMessaInMora || data.ultimaInterruzione ? data.scadenzaPrescrizione : prescriptionFromDate(e.target.value)})} required />
            </div>
            <div className="input-container"><div className="input-label"><label htmlFor="accident-time" title="Facoltativa">Ora del sinistro</label></div><input id="accident-time" type="time" value={data.oraSinistro || ''} onChange={e => handleChange('oraSinistro', e.target.value)} /></div>
            </div>
            {isAccidentPolicy && <div className="input-container policy-dynamics"><label>Dinamica</label><textarea data-autoformat="sentence" rows="5" data-autoformat="sentence" value={data.dinamica || ''} onChange={e => handleChange('dinamica', e.target.value)} required /></div>}
            {!isAccidentPolicy && <div className="prescription-date-row">
              <div className="input-container">
                <label htmlFor="demand-date">Messa in mora</label>
                <input id="demand-date" type="date" value={data.dataMessaInMora || suggestedDemandDate} onChange={e => setDemandDate(e.target.value)} />
                {!data.dataMessaInMora && <div className="demand-date-suggestion"><small>Data di apertura suggerita · da confermare</small><button type="button" onClick={() => setDemandDate(suggestedDemandDate)}>Conferma data</button></div>}
              </div>
              <div className="input-container">
                <label htmlFor="prescription-date">Prescrizione</label>
                <input id="prescription-date" type="date" value={data.scadenzaPrescrizione || ''} readOnly={!isHealthcare} onChange={e=>handleChange('scadenzaPrescrizione',e.target.value)} />
                <small>{isHealthcare?'Data da valutare e indicare per il caso concreto.':data.ultimaInterruzione && data.ultimaInterruzione > (data.dataMessaInMora || '') ? 'Calcolata dall’ultima interruzione registrata.' : data.dataMessaInMora ? '2 anni dalla messa in mora.' : 'Scadenza attuale: conferma la messa in mora per aggiornarla.'}</small>
              </div>
            </div>}
            {vehicleTerms && <div className="input-container cai-signatures-box"><strong>CAI</strong><SlidingChoice label="Firma CAI" value={caiTipo} options={[{value:'assente',label:'Assente'},{value:'monofirma',label:'Monofirma'},{value:'doppia',label:'Doppia firma'}]} onChange={value => setData(previous => ({ ...previous, caiTipo: value, caiDueFirme: value === 'doppia' }))} /></div>}
            {!isAccidentPolicy && !isHealthcare && <PracticeDocumentLinks schedaId={schedaId} suspendedId={suspendedId} title="Documenti del sinistro" documentKeys={vehicleTerms && caiTipo !== 'assente' ? ['cai', 'accidentPhotos'] : ['accidentPhotos']} />}
          </div>
        </div>

        <div className="box">
          <h2>{isAccidentPolicy ? 'Polizza' : isHealthcare?'Fatti da approfondire':'Autorità e dinamica'}</h2>
          <div className="sinistro-form">
            {!isAccidentPolicy && <><div className="input-container">
              <textarea placeholder="Dinamica" rows="4" data-autoformat="sentence" value={data.dinamica || ''}
                onChange={(e) => handleChange('dinamica', e.target.value)} required />
            </div>
            {!isHealthcare&&<><div className="input-container authority-toggle-box">
              <label className="authority-toggle-row">
                <span><strong>Sono intervenute autorità</strong><small>Abilita i dati e i documenti dell'autorità intervenuta.</small></span>
                <span className="vehicle-ios-toggle cisa-switch">
                  <input type="checkbox" role="switch" aria-label="Sono intervenute autorità" checked={autoritaPresenti}
                    onChange={event => setAutoritaPresenti(event.target.checked)} />
                  <i aria-hidden="true" />
                  <b>{autoritaPresenti ? 'Sì' : 'No'}</b>
                </span>
              </label>
            </div>
            {autoritaPresenti && <>
              <div className="input-container authority-details">
                <label>Autorità intervenuta</label>
                <select value={data.autoritaIntervenuta || ''}
                  onChange={(e) => handleChange('autoritaIntervenuta', e.target.value)} required>
                  <option value="">Da indicare</option>
                  <option value="Polizia Locale">Polizia Locale</option>
                  <option value="Carabinieri">Carabinieri</option>
                  <option value="Polizia Stradale">Polizia Stradale</option>
                </select>
              </div>
              <div className="input-container authority-details">
                <label>Città dell'autorità</label>
                <ComuneAutocomplete placeholder="Scrivi la città" value={data.cittaAutorita || ''}
                  onChange={value => handleChange('cittaAutorita', value)} required />
              </div>
            </>}
            </>}<div className="input-container responsibility-field">
              <label htmlFor="responsabilita-controparte">Responsabilità della controparte</label>
              <div><input id="responsabilita-controparte" inputMode="decimal" placeholder="Da definire"
                value={data.responsabilitaControparte ?? ''}
                onFocus={event => { event.currentTarget.dataset.previous = event.currentTarget.value; }}
                onChange={event => { if (/^\d{0,3}([,.]\d{0,2})?$/.test(event.target.value)) handleChange('responsabilitaControparte', event.target.value); }}
                onBlur={event => {
                  const raw = event.currentTarget.value.replace(',', '.');
                  const previous = event.currentTarget.dataset.previous || '';
                  if (raw && (Number(raw) < 0 || Number(raw) > 100)) { handleChange('responsabilitaControparte', previous); return; }
                  if (previous !== '' && previous !== event.currentTarget.value && !window.confirm('Modificare la responsabilità della controparte? Tutte le quantificazioni non ancora definite saranno ricalcolate.')) handleChange('responsabilitaControparte', previous);
                }} />
                <span>%</span>
              </div>
              <small>Il programma applicherà questa percentuale ai danni ancora da definire.</small>
            </div>
            {showDocumentLinks && autoritaPresenti && data.autoritaIntervenuta && <PracticeDocumentLinks schedaId={schedaId} suspendedId={suspendedId} title="Documenti delle autorità" documentKeys={['detailsExchange', 'accidentReport']} />}
            </>}
            {isAccidentPolicy && <>
              <div className="input-container"><label>Compagnia</label><CompanyAutocomplete ariaLabel="Cerca compagnia della polizza"
                value={data.nomeCompagnia || ''} companies={compagnieAssicurative}
                onSelect={company => handlePolicyCompanyChange(String(company.id))} onCreate={name => openNewCompany(-1, name)} /></div>
              <div className="input-container"><label>Numero di polizza</label><input value={data.numeroPolizza || ''} onChange={e => handleChange('numeroPolizza', e.target.value)} /></div>
              <div className="input-container"><label>Contraente della polizza, se diverso dal cliente</label><input value={data.contraentePolizza || ''} onChange={e => handleChange('contraentePolizza', e.target.value)} /></div>
              <div className="input-container"><label>Numero del sinistro assegnato dalla compagnia</label><input value={data.numeroSinistroCompagnia || ''} onChange={e => handleChange('numeroSinistroCompagnia', e.target.value)} /></div>
            </>}
          </div>
        </div>
      </div>

      {isOtherLiability && <div className="box"><h2>Controparte e assicurazione</h2><div className="sinistro-form"><CounterpartyFields value={data.controparte||{}} onChange={value=>setData(previous=>({...previous,controparte:value}))}/><div className="input-container"><label>Compagnia assicuratrice</label><CompanyAutocomplete ariaLabel="Compagnia della controparte" value={currentCompanyName(data.compagniaId,data.nomeCompagnia)} companies={compagnieAssicurative} onSelect={company=>setData(previous=>({...previous,compagniaId:company.id,nomeCompagnia:company.nome}))} onCreate={name=>openNewCompany(-1,name)}/></div><label className="input-container">Numero di polizza<input value={data.numeroPolizza||''} onChange={e=>handleChange('numeroPolizza',e.target.value)}/></label><label className="input-container">Numero sinistro della compagnia<input value={data.numeroSinistroCompagnia||''} onChange={e=>handleChange('numeroSinistroCompagnia',e.target.value)}/></label></div></div>}
      {isHealthcare&&<HealthcareCase value={data.sanitario||{}} onChange={value=>handleChange('sanitario',value)} schedaId={schedaId} suspendedId={suspendedId} renderCompany={(person,change)=><><div className="input-container"><label>Compagnia assicuratrice</label><CompanyAutocomplete ariaLabel="Compagnia altra controparte" value={currentCompanyName(person.compagniaId,person.compagnia)} companies={compagnieAssicurative} onSelect={c=>change({compagniaId:c.id,compagnia:c.nome})} onCreate={()=>setCompanyError('Aggiungi prima la nuova compagnia in Rubrica, poi selezionala qui.')}/></div><label className="input-container">Numero di polizza<input value={person.numeroPolizza||''} onChange={e=>change({numeroPolizza:e.target.value})}/></label></>}/>}
      {!isAccidentPolicy && !isHealthcare && <div className="box mezzi-section">
        <div className="mezzi-heading">
          <h2>Mezzi coinvolti</h2>
          <button type="button" className="add-button" onClick={aggiungiMezzo}>+ {isPedestrian ? 'Aggiungi veicolo di controparte' : 'Aggiungi veicolo'}</button>
        </div>
        <div className="mezzi-grid">
        {(data.mezzi || []).map((mezzo, index) => (
          <div key={index} className="mezzo-container">
            <div className="mezzo-heading"><h3>{vehicleLabel(index)}</h3>
              {(index > 0 || isOtherLiability) && <button type="button" className="remove-button" onClick={() => rimuoviMezzo(index)}>Rimuovi</button>}
            </div>
            {(index === 0 || isPedestrian) && <div className="input-container vehicle-field">
              <label htmlFor={`tipo-mezzo-${index}`}>Tipo di mezzo</label>
              <select id={`tipo-mezzo-${index}`} value={mezzo.tipoMezzo || 'auto'} onChange={e => handleChange('tipoMezzo', e.target.value, index)}>
                <option value="auto">Auto</option><option value="moto">Moto</option><option value="velocipede">Bicicletta</option><option value="monopattino">Monopattino</option>{index===0 && tipoPratica==='RCA' && <option value="cose">Altro bene danneggiato</option>}
              </select>
            </div>}
            {mezzo.tipoMezzo==='cose' ? <><label className="input-container">Bene danneggiato<input value={mezzo.veicolo||''} placeholder="Cancello, recinzione, fotocellule o qualunque altro bene" onChange={e=>handleChange('veicolo',e.target.value,index)}/></label><label className="input-container">Ubicazione<input value={mezzo.ubicazione??data.luogoSinistro??''} onChange={e=>handleChange('ubicazione',e.target.value,index)}/></label><label className="input-container">Descrizione dei danni<textarea value={mezzo.descrizioneDanni||''} onChange={e=>handleChange('descrizioneDanni',e.target.value,index)}/></label></> : <>
            <div className="input-container vehicle-field">
              <label htmlFor={`proprietario-${index}`}>Proprietario</label>
              <input id={`proprietario-${index}`} type="text" placeholder="Nome e cognome" value={mezzo.proprietario || ''}
                onChange={(e) => handleChange('proprietario', e.target.value, index)} required />
            </div>
            <div className="input-container vehicle-field">
              <label htmlFor={`veicolo-${index}`}>Veicolo</label>
              <input id={`veicolo-${index}`} type="text" placeholder="Marca e modello" value={mezzo.veicolo || ''}
                onChange={(e) => handleChange('veicolo', e.target.value, index)} required />
            </div>
            <div className="input-container vehicle-field">
              <label htmlFor={`targa-${index}`}>Targa</label>
              <input id={`targa-${index}`} type="text" placeholder={['velocipede','monopattino'].includes(mezzo.tipoMezzo)?'Facoltativa':'Targa'} value={mezzo.targa || ''}
                onChange={(e) => handleChange('targa', e.target.value.toUpperCase(), index)} required />
            </div>
            <div className="input-container vehicle-field">
              <div className="vehicle-field-heading">
                <label htmlFor={`conducente-${index}`}>Conducente</label>
                <label className="same-person-check vehicle-ios-toggle cisa-switch">
                  <span>Stesso proprietario</span>
                  <input type="checkbox" role="switch" checked={Boolean(mezzo.conducenteComeProprietario)}
                    onChange={event => impostaConducenteComeProprietario(index, event.target.checked)} />
                  <i aria-hidden="true" />
                  <b>{mezzo.conducenteComeProprietario ? 'Sì' : 'No'}</b>
                </label>
              </div>
              <input id={`conducente-${index}`} type="text" placeholder="Nome e cognome" value={mezzo.conducente || ''}
                onChange={(e) => handleChange('conducente', e.target.value, index)}
                disabled={Boolean(mezzo.conducenteComeProprietario)} required />
            </div>
            {!isOtherLiability && <>
            <div className="input-container">
              <CompanyAutocomplete ariaLabel={`Cerca compagnia mezzo ${index + 1}`} value={currentCompanyName(mezzo.compagniaId, mezzo.compagnia || '')}
                companies={compagnieAssicurative} onSelect={company => handleCompagniaChange(String(company.id), index)}
                onCreate={name => openNewCompany(index, name)} />
              {mezzo.compagnia && !mezzo.compagniaId && <small>Nome precedente: {mezzo.compagnia}</small>}
            </div>
            <div className="input-container claim-number-field">
              <label htmlFor={`numero-sinistro-${index}`}>Numero sinistro della compagnia</label>
              <input id={`numero-sinistro-${index}`} type="text" placeholder="Da assegnare"
                value={mezzo.numeroSinistro || ''} onChange={e => handleChange('numeroSinistro', e.target.value, index)} />
            </div>
            <div className="input-container">
              <label className="risarcitoria-toggle-row">
                <span>Compagnia risarcitoria</span>
                <span className="vehicle-ios-toggle cisa-switch">
                  <input type="checkbox" aria-label={`Compagnia risarcitoria veicolo ${index + 1}`}
                    checked={Boolean(mezzo.isRisarcitoria)} onChange={() => setRisarcitoria(index)} />
                  <i aria-hidden="true" />
                  <b>{mezzo.isRisarcitoria ? 'Sì' : 'No'}</b>
                </span>
              </label>
            </div>
            </>}
            </>}
            {index === 0 && !isPedestrian && !['cose','velocipede','monopattino'].includes(mezzo.tipoMezzo) && <PracticeDocumentLinks schedaId={schedaId} suspendedId={suspendedId} title="Documento del veicolo" documentKeys={['registration']} />}
          </div>
        ))}
        </div>
      </div>}

      {showBanner && <CompanyContactModal value={newCompagnia} onChange={setNewCompagnia}
        onClose={() => setShowBanner(false)} onSubmit={handleSaveNewCompagnia} error={companyError}
        submitLabel="Salva e seleziona" />}
    </div>
  );
};

export default DatiSinistro;
